import { createContext, Fragment, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import type { PlanItemDTO } from '@clear/contracts';
import {
  blocker,
  floorQueues,
  HOME_PATH,
  isGateRole,
  isHold,
  minutesToHHMM,
  readyQty,
  type Role,
} from '@clear/domain';
import { LANGUAGE_NAMES, LANGUAGES, type MessageKey } from '@clear/i18n';
import { Avatar, Button, Empty, Icon, Logo, Sheet, type IconName } from '@clear/ui';
import { ROLE_COLOUR, useCurrentShift, useLookups, useSelectedLine, type Lookups } from '../data/bootstrap';
import { NO_ITEMS, usePlan } from '../data/plan';
import { useI18n } from '../i18n';
import { useLiveStatus } from '../live/stream';
import { useMedia, useTick } from '../lib/media';
import { storage } from '../lib/storage';
import { useGoBack } from '../lib/nav';
import { applyTheme, savedTheme, type Theme } from '../lib/theme';
import { fmtDate, hhmm, now } from '../lib/time';
import { useSyncStatus } from '../offline/hooks';
import { useAuthed, useSession } from '../session';
import { ItemSheet } from './sheets';

/** Phones get the native-app shell (app bar + bottom tabs); laptops the wide layout. */
export const PHONE_QUERY = '(max-width: 759px)';
export const useIsPhone = () => useMedia(PHONE_QUERY);

function useSyncState() {
  const s = useSyncStatus();
  const live = useLiveStatus();
  const tone = !s.online ? 'hold' : s.pending ? 'wait' : live === 'open' ? 'ok' : 'wait';
  return { s, tone };
}

/** Web header: sync status next to the clock (Synced · N to sync · Offline). */
function HeaderSync() {
  const { t } = useI18n();
  const { s, tone } = useSyncState();
  const label = !s.online
    ? s.pending
      ? `${t('shell.offline')} · ${t('shell.pending', { n: s.pending })}`
      : t('shell.offline')
    : s.pending
      ? t('shell.pending', { n: s.pending })
      : t('shell.synced');
  return (
    <span
      className={`web__sync web__sync--${tone}`}
      data-pending={s.pending}
      role="status"
      aria-live="polite"
      title={!s.online ? t('shell.offlineQueued') : undefined}
    >
      <Icon name={!s.online ? 'offline' : s.pending ? 'sync' : 'check'} />
      {label}
    </span>
  );
}

/** Prototype's phone sync pill: status dot + clock, or the pending count. */
function SyncPill({ tz }: { tz: string }) {
  const { t } = useI18n();
  const { s, tone } = useSyncState();
  useTick(30_000);
  return (
    <span
      className={`m-sync m-sync--${tone}`}
      data-pending={s.pending}
      role="status"
      aria-live="polite"
      title={!s.online ? t('shell.offlineQueued') : undefined}
    >
      <i className="m-sync__dot" aria-hidden="true" />
      {s.pending ? t('shell.pending', { n: s.pending }) : !s.online ? t('shell.offline') : hhmm(now(), tz)}
    </span>
  );
}

/**
 * ?item=<id> opens a line's timeline (deep-linkable). Opening pushes a history entry so
 * Back closes it; closing from the UI steps back over that same entry, so the next Back
 * is never a dead press. A deep link opened directly just drops the parameter.
 */
export function useItemParam(): [string | null, (id: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const loc = useLocation();
  const navigate = useNavigate();
  const id = params.get('item');
  return [
    id,
    (next) => {
      const p = new URLSearchParams(params);
      if (next) {
        p.set('item', next);
        setParams(p, { state: { itemSheet: true } });
      } else if ((loc.state as { itemSheet?: boolean } | null)?.itemSheet) {
        void navigate(-1);
      } else {
        p.delete('item');
        setParams(p, { replace: true });
      }
    },
  ];
}

/** Renders the item timeline sheet when ?item=<id> is present (deep-linkable). */
export function ItemSheetHost({
  items,
  lk,
  canEdit = false,
}: {
  items: PlanItemDTO[];
  lk: Lookups;
  canEdit?: boolean;
}) {
  const [id, setId] = useItemParam();
  const item = id ? items.find((i) => i.id === id) : undefined;
  if (!item) return null;
  return <ItemSheet item={item} lk={lk} canEdit={canEdit} onClose={() => setId(null)} />;
}

/* ================= profile (Me) ================= */

/** Role switch, language, theme, line, sync and logout — the Me tab / profile sheet. */
export function ProfileContent({ onDone }: { onDone?: () => void }) {
  const { t, lang, setLang } = useI18n();
  const session = useAuthed();
  const { logout, switchRole } = useSession();
  const lk = useLookups();
  const cur = useCurrentShift(lk);
  const [line, setLine] = useSelectedLine(lk);
  const [theme, setTheme] = useState<Theme>(savedTheme());
  const { s } = useSyncState();
  const navigate = useNavigate();
  const u = session.user;
  const row = { gridTemplateColumns: '1fr auto' };
  return (
    <>
      <div className="list">
        {u.roles.length > 1 && (
          <div className="row" style={row}>
            <span className="row__title">{t('shell.workingAs')}</span>
            <div className="seg">
              {u.roles.map((r) => (
                <button
                  key={r}
                  type="button"
                  aria-pressed={session.role === r}
                  onClick={() => {
                    void switchRole(r);
                    onDone?.();
                    void navigate(HOME_PATH[r]);
                  }}
                >
                  {t(`roles.${r}`)}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="row" style={row}>
          <span className="row__title">{t('shell.language')}</span>
          <div className="seg">
            {LANGUAGES.map((l) => (
              <button
                key={l}
                type="button"
                aria-pressed={lang === l}
                onClick={() => {
                  setLang(l);
                  void import('../lib/api').then(({ api }) =>
                    api('/auth/language', { body: { language: l } }).catch(() => undefined),
                  );
                }}
              >
                {LANGUAGE_NAMES[l]}
              </button>
            ))}
          </div>
        </div>
        <div className="row" style={row}>
          <span className="row__title">{t('shell.theme')}</span>
          <div className="seg">
            {(['auto', 'light', 'dark'] as Theme[]).map((x) => (
              <button
                key={x}
                type="button"
                aria-pressed={theme === x}
                onClick={() => {
                  applyTheme(x);
                  setTheme(x);
                }}
              >
                {t(`shell.theme${x === 'auto' ? 'Auto' : x === 'light' ? 'Light' : 'Dark'}`)}
              </button>
            ))}
          </div>
        </div>
        {lk && lk.activeLines.length > 1 && (
          <div className="row" style={row}>
            <span className="row__title">{t('shell.line')}</span>
            <div className="seg">
              {lk.activeLines.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  aria-pressed={line?.id === l.id}
                  onClick={() => {
                    setLine(l.id);
                    window.location.reload();
                  }}
                >
                  {l.name}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="row" style={row}>
          <span className="row__title">{t('shell.sync')}</span>
          <span className="muted">
            {s.pending
              ? t('shell.pending', { n: s.pending })
              : !s.online
                ? t('shell.offline')
                : lk
                  ? t('shell.allSaved', { time: hhmm(now(), lk.tz) })
                  : t('shell.synced')}
          </span>
        </div>
        {lk && cur && (
          <div className="row" style={row}>
            <span className="row__title">{t('shell.shiftRow')}</span>
            <span className="muted">
              {cur.shift.code} · {minutesToHHMM(cur.shift.startMin)}–{minutesToHHMM(cur.shift.endMin)}
              {line ? ` · ${line.name}` : ''}
            </span>
          </div>
        )}
      </div>
      <Button
        variant="danger"
        size="lg"
        block
        icon="out"
        onClick={() => {
          onDone?.();
          void logout().then(() => navigate('/login', { replace: true }));
        }}
      >
        {t('shell.logout')}
      </Button>
      <p className="m-foot">
        {session.idleTimeoutMs
          ? t('shell.sharedIdle')
          : lk
            ? t('shell.sessionEnds', { time: hhmm(session.expiresAt, lk.tz) })
            : null}
        {lk ? ` · ${t('shell.version', { v: lk.b.appVersion })}` : ''}
      </p>
    </>
  );
}

function ProfileSheet({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const session = useAuthed();
  const u = session.user;
  return (
    <Sheet
      title={u.name}
      subtitle={`${u.title}${u.ext ? ` · ${t('common.ext', { ext: u.ext })}` : ''}`}
      media={<Avatar name={u.name} photoUrl={u.photoUrl} colour={ROLE_COLOUR[session.role]} />}
      onClose={onClose}
      closeLabel={t('common.close')}
    >
      <ProfileContent onDone={onClose} />
    </Sheet>
  );
}

/* ================= role navigation ================= */

export interface NavItem {
  key: string;
  to: string;
  label: string;
  /** Title for the app bar / page header when this item is active (defaults to the label). */
  title?: string;
  icon: IconName;
  badge?: number;
  tone?: 'hold';
  active: boolean;
  /** Sidebar section heading this item starts (web layout). */
  group?: string;
}

type Form = 'phone' | 'web';

/**
 * Navigation for each role, with live badges (To Do count, issues, ready count, holds)
 * derived from the same cached plan the page shows. Phones get the prototype's bottom
 * tabs; the web layout gets a sidebar with the same destinations (plus Admin setup
 * sections and the Floor Display).
 */
function useRoleNav(form: Form): NavItem[] {
  const { t } = useI18n();
  const session = useAuthed();
  const lk = useLookups();
  const [line] = useSelectedLine(lk);
  const plan = usePlan({ lineId: line?.id });
  const loc = useLocation();
  const [params] = useSearchParams();
  const items = plan.data?.plan?.status === 'published' ? plan.data.items : NO_ITEMS;
  const role = session.role;
  const path = loc.pathname;
  const view = params.get('view');

  return useMemo(() => {
    const mk = (
      key: string,
      to: string,
      label: MessageKey,
      icon: IconName,
      active: boolean,
      extra: Partial<NavItem> = {},
    ): NavItem => ({ key, to, label: t(label), icon, active, ...extra });
    const reports = mk('reports', '/reports', 'dashboard.reports', 'chart', path.startsWith('/reports'));
    const laptop = mk('laptop', '/display', 'laptop.name', 'screen', false);
    const holds = items.filter((i) => {
      if (i.cancelled || i.startedAt != null) return false;
      const b = blocker(i);
      return !!b && isHold(i, b.gate);
    }).length;

    if (isGateRole(role)) {
      const q = floorQueues(items, role);
      return [
        mk('todo', `/${role}`, 'floor.todo', 'list', path === `/${role}`, { badge: q.todo.length }),
        mk('issues', `/${role}/issues`, 'floor.issues', 'warn', path === `/${role}/issues`, {
          badge: q.issues.length,
          tone: 'hold',
        }),
        mk('done', `/${role}/done`, 'floor.done', 'check', path === `/${role}/done`),
      ];
    }
    if (role === 'planner') {
      return [
        mk('plan', '/plan', 'planner.plan', 'list', path === '/plan', { title: t('planner.title') }),
        mk('dash', '/dashboard', 'planner.dashboard', 'grid', path === '/dashboard', {
          title: t('dashboard.title'),
          badge: holds,
          tone: 'hold',
        }),
        reports,
      ];
    }
    if (role === 'supervisor') {
      const live = items.filter((i) => !i.cancelled);
      const ready = mk('ready', '/run', 'supervisor.readyTab', 'play', path === '/run', {
        title: t('supervisor.title'),
        badge: live.filter((i) => i.startedAt == null && readyQty(i) > 0).length,
      });
      if (form === 'web')
        return [{ ...ready, label: t('supervisor.title'), active: path.startsWith('/run') }];
      return [
        ready,
        mk('up', '/run/up', 'supervisor.comingUp', 'clock', path === '/run/up'),
        mk('started', '/run/started', 'supervisor.started', 'check', path === '/run/started'),
      ];
    }
    if (role === 'mgmt') {
      if (form === 'web') {
        return [
          mk('dash', '/dashboard', 'dashboard.title', 'grid', path === '/dashboard', {
            badge: holds,
            tone: 'hold',
          }),
          reports,
          laptop,
        ];
      }
      const onDash = path === '/dashboard';
      return [
        mk('overview', '/dashboard', 'dashboard.overview', 'grid', onDash && !view),
        mk('lines', '/dashboard?view=lines', 'dashboard.lines', 'list', onDash && view === 'lines'),
        mk('holds', '/dashboard?view=holds', 'dashboard.holdsTab', 'stop', onDash && view === 'holds', {
          badge: holds,
          tone: 'hold',
        }),
        reports,
      ];
    }
    // admin
    if (form === 'web') {
      const sec = path.startsWith('/admin') ? (path.split('/')[2] ?? 'users') : null;
      const a = (key: string, label: MessageKey, icon: IconName, extra: Partial<NavItem> = {}) =>
        mk(key, key === 'users' ? '/admin' : `/admin/${key}`, label, icon, sec === key, extra);
      return [
        a('users', 'admin.users', 'user', { group: t('admin.title') }),
        a('models', 'admin.models', 'box'),
        a('reasons', 'admin.reasons', 'warn'),
        a('shifts', 'admin.shifts', 'clock'),
        a('lines', 'admin.lines', 'list'),
        a('devices', 'admin.devices', 'phone'),
        a('audit', 'admin.audit', 'doc'),
        mk('dash', '/dashboard', 'dashboard.title', 'grid', path === '/dashboard', {
          group: t('shell.views'),
          badge: holds,
          tone: 'hold',
        }),
        reports,
        laptop,
      ];
    }
    return [mk('setup', '/admin', 'admin.title', 'gear', path.startsWith('/admin')), laptop, reports];
  }, [t, role, items, path, view, form]);
}

interface ShellProps {
  title: string;
  /** Subtitle for the app bar / page header; defaults to line · shift · date. */
  sub?: string;
  waiting?: number;
  wide?: boolean;
  actions?: ReactNode;
  children: ReactNode;
}

function MobileShell({ title, sub, children }: ShellProps) {
  const { t } = useI18n();
  const session = useAuthed();
  const lk = useLookups();
  const [line] = useSelectedLine(lk);
  const cur = useCurrentShift(lk);
  const nav = useRoleNav('phone');
  const loc = useLocation();
  const onMe = loc.pathname === '/me';
  const active = nav.find((n) => n.active);
  const u = session.user;
  const barTitle = onMe ? t('shell.me') : (active?.title ?? active?.label ?? title);
  const barSub =
    sub ??
    [t(`roles.${session.role}`), line?.name, cur ? t('common.shift', { code: cur.shift.code }) : '']
      .filter(Boolean)
      .join(' · ');

  useEffect(() => {
    document.body.classList.add('m-app');
    return () => document.body.classList.remove('m-app');
  }, []);

  return (
    <ShellSlots.Provider value={PHONE_SLOTS}>
      <div className="mroot">
        <header className="m-bar">
          <div className="m-bar__ttl">
            <h1 className="display">
              {barTitle}
              {lk?.b.demo && <span className="demo-chip">{t('demo.badge')}</span>}
            </h1>
            <div className="m-bar__sub">{barSub}</div>
          </div>
          {lk && <SyncPill tz={lk.tz} />}
          <Link to="/me" className="m-avbtn" aria-label={t('a11y.menu')}>
            <Avatar name={u.name} photoUrl={u.photoUrl} colour={ROLE_COLOUR[session.role]} size="sm" />
          </Link>
        </header>
        <main
          id="main"
          className="m-body"
          tabIndex={-1}
          key={loc.pathname + (new URLSearchParams(loc.search).get('view') ?? '')}
        >
          {children}
        </main>
        <nav className="m-nav" aria-label={t('shell.workingAs')}>
          {nav.map((n) => (
            <Link key={n.key} to={n.to} aria-current={n.active ? 'page' : undefined}>
              <span className="m-nav__ic">
                <Icon name={n.icon} />
              </span>
              {n.label}
              {n.badge ? (
                <span className={`m-nav__badge${n.tone ? ` m-nav__badge--${n.tone}` : ''}`}>{n.badge}</span>
              ) : null}
            </Link>
          ))}
          <Link to="/me" aria-current={onMe ? 'page' : undefined}>
            <span className="m-nav__ic">
              <Icon name="user" />
            </span>
            {t('shell.me')}
          </Link>
        </nav>
      </div>
    </ShellSlots.Provider>
  );
}

/* ================= web layout: sidebar + page header ================= */

interface Slots {
  phone: boolean;
  /** Header element page actions are portalled into (web layout). */
  actions: HTMLElement | null;
}
const PHONE_SLOTS: Slots = { phone: true, actions: null };
const ShellSlots = createContext<Slots>(PHONE_SLOTS);

const COLLAPSE_KEY = 'clear.sidebar';

function Sidebar({ nav, onProfile }: { nav: NavItem[]; onProfile: () => void }) {
  const { t } = useI18n();
  const session = useAuthed();
  const { switchRole } = useSession();
  const lk = useLookups();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => storage.get(COLLAPSE_KEY) === '1');
  const narrow = useMedia('(max-width: 1099px)');
  const rail = collapsed || narrow;
  const u = session.user;
  return (
    <aside className={`side${rail ? ' side--rail' : ''}`} aria-label={t('app.name')}>
      <div className="side__brand">
        <span className="side__logo" aria-hidden="true">
          <Logo size={26} />
        </span>
        <span className="side__brandtext">
          <b className="display">{t('app.name')}</b>
          <span>{lk?.b.plant.name ?? t('app.product')}</span>
          {lk?.b.demo && <span className="demo-chip demo-chip--side">{t('demo.badge')}</span>}
        </span>
      </div>

      {u.roles.length > 1 && (
        <div className="side__roles" role="group" aria-label={t('shell.workingAs')}>
          {u.roles.map((r: Role) => (
            <button
              key={r}
              type="button"
              aria-pressed={session.role === r}
              title={t(`roles.${r}`)}
              onClick={() => {
                void switchRole(r);
                void navigate(HOME_PATH[r]);
              }}
            >
              <span className="side__roledot" style={{ background: ROLE_COLOUR[r] }} />
              <span className="side__label">{t(`roles.${r}`)}</span>
            </button>
          ))}
        </div>
      )}

      <nav className="side__nav">
        {nav.map((n) => (
          <Fragment key={n.key}>
            {n.group && <p className="side__group">{n.group}</p>}
            <Link
              to={n.to}
              className="side__item"
              aria-current={n.active ? 'page' : undefined}
              title={rail ? n.label : undefined}
            >
              <span className="side__ic">
                <Icon name={n.icon} />
                {rail && n.badge ? (
                  <span className={`side__dot${n.tone ? ' side__dot--hold' : ''}`}>{n.badge}</span>
                ) : null}
              </span>
              <span className="side__label">{n.label}</span>
              {!rail && n.badge ? (
                <span className={`count${n.tone ? ' count--hold' : ''}`}>{n.badge}</span>
              ) : null}
            </Link>
          </Fragment>
        ))}
      </nav>

      <div className="side__foot">
        <button type="button" className="side__user" onClick={onProfile} aria-label={t('a11y.menu')}>
          <Avatar name={u.name} photoUrl={u.photoUrl} colour={ROLE_COLOUR[session.role]} size="sm" />
          <span className="side__label side__who">
            <b>{u.name}</b>
            <span>{t(`roles.${session.role}`)}</span>
          </span>
        </button>
        {!narrow && (
          <button
            type="button"
            className="side__collapse"
            onClick={() => {
              storage.set(COLLAPSE_KEY, collapsed ? '0' : '1');
              setCollapsed(!collapsed);
            }}
            aria-label={collapsed ? t('shell.expand') : t('shell.collapse')}
            title={collapsed ? t('shell.expand') : t('shell.collapse')}
          >
            <Icon name={collapsed ? 'next' : 'back'} />
          </button>
        )}
      </div>
    </aside>
  );
}

/**
 * Web layout (≥ 760 px): a sidebar with the role's destinations and live badges, a
 * sticky page header with title, line · shift · date, page actions and the clock,
 * and a full-width content area.
 */
function WebShell({ title, sub, waiting, wide, actions, children }: ShellProps) {
  const { t, lang } = useI18n();
  const lk = useLookups();
  const [line] = useSelectedLine(lk);
  const cur = useCurrentShift(lk);
  const nav = useRoleNav('web');
  const [profile, setProfile] = useState(false);
  const [actionsEl, setActionsEl] = useState<HTMLElement | null>(null);
  const loc = useLocation();
  useTick(30_000);
  const active = nav.find((n) => n.active);
  const heading = loc.pathname === '/me' ? t('shell.me') : (active?.title ?? title);
  const subline =
    sub ??
    [line?.name, cur ? t('common.shift', { code: cur.shift.code }) : '', cur ? fmtDate(cur.date, lang) : '']
      .filter(Boolean)
      .join(' · ');
  const slots = useMemo(() => ({ phone: false, actions: actionsEl }), [actionsEl]);
  useEffect(() => {
    document.body.classList.add('w-app');
    return () => document.body.classList.remove('w-app');
  }, []);
  return (
    <ShellSlots.Provider value={slots}>
      <div className="web">
        <a className="skip-link" href="#main">
          {t('a11y.skip')}
        </a>
        <Sidebar nav={nav} onProfile={() => setProfile(true)} />
        <div className="web__main">
          <header className="web__head">
            <div className="web__title">
              <h1 className="display">{heading}</h1>
              <p>{subline}</p>
            </div>
            <div className="web__actions" ref={setActionsEl} />
            {actions}
            {waiting != null && waiting > 0 && (
              <span className="waiting" title={t('shell.waitingOnYou', { n: waiting })}>
                {t('shell.waitingOnYou', { n: waiting })}
              </span>
            )}
            <HeaderSync />
            {lk && <span className="web__clock num">{hhmm(now(), lk.tz)}</span>}
          </header>
          <main id="main" className={`web__content${wide ? ' web__content--wide' : ''}`} tabIndex={-1}>
            {children}
          </main>
        </div>
        {profile && <ProfileSheet onClose={() => setProfile(false)} />}
      </div>
    </ShellSlots.Provider>
  );
}

export function Shell(props: ShellProps) {
  const phone = useIsPhone();
  const guarded = { ...props, children: <NeedsActiveLine>{props.children}</NeedsActiveLine> };
  return phone ? <MobileShell {...guarded} /> : <WebShell {...guarded} />;
}

/**
 * Plans hang off an assembly line: with every line turned off there is nothing to plan
 * or clear, so say why instead of showing empty screens. Setup and profile stay usable.
 */
function NeedsActiveLine({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const lk = useLookups();
  const loc = useLocation();
  const { session } = useSession();
  if (!lk || lk.b.lines.some((l) => l.active)) return <>{children}</>;
  if (loc.pathname.startsWith('/admin') || loc.pathname === '/me') return <>{children}</>;
  const isAdmin = !!session?.user.roles.includes('admin');
  return (
    <Empty title={t('shell.noLine')}>
      {isAdmin ? t('shell.noLineAdmin') : t('shell.noLineHint')}
      {isAdmin && (
        <>
          <br />
          <Link className="btn btn--primary" to="/admin/lines" style={{ marginTop: 12 }}>
            {t('shell.openLines')}
          </Link>
        </>
      )}
    </Empty>
  );
}

/**
 * Page title + toolbar. On phones the app bar carries the title and the toolbar sits
 * at the top of the page; on the web the title is in the page header and the toolbar
 * buttons move into the header's action area.
 */
export function PageHead({
  title,
  sub,
  children,
}: {
  title: ReactNode;
  sub?: ReactNode;
  children?: ReactNode;
}) {
  const slots = useContext(ShellSlots);
  if (!slots.phone) return slots.actions && children ? createPortal(children, slots.actions) : null;
  return (
    <div className="pagehead">
      <div className="pagehead__text">
        <h1 className="display pagehead__title">{title}</h1>
        {sub && <p className="pagehead__sub">{sub}</p>}
      </div>
      {children}
    </div>
  );
}

/**
 * The page's primary actions (All OK, Publish, Add line): floating buttons above the
 * tabs on phones, buttons in the page header on the web.
 */
export function PageActions({ children }: { children: ReactNode }) {
  const slots = useContext(ShellSlots);
  if (!slots.phone) return slots.actions ? createPortal(children, slots.actions) : null;
  return <div className="dock">{children}</div>;
}

/** Back button for sub-pages: returns to the previous page (or `to` when opened directly). */
export function BackLink({ to, label }: { to: string; label: string }) {
  const goBack = useGoBack(to);
  return (
    <button type="button" className="btn btn--ghost btn--sm" onClick={goBack}>
      <Icon name="back" />
      {label}
    </button>
  );
}
